import Checkbox from "@mui/material/Checkbox";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import clsx from "clsx";
import { PREREQUISITES } from "core/consts";
import { useGetOntologies } from "core/model/utils/ontologies/hooks";
import { GetOntologiesType } from "core/types";
import { getOrderedItems } from "ds_legacy/components/ChipGroup";
import { labelWithOptionalRequiredIcon } from "ds_legacy/components/RequiredIcon";
import ToggleInputField from "ds_legacy/components/ToggleInputField";
import { Validation } from "ds_legacy/components/Validation";
import { margin, padding, space } from "ds_legacy/materials/metrics";
import { FONT_SIZE_14, Subheading } from "ds_legacy/materials/typography";
import memoizeOne from "memoize-one";
import React, { Fragment } from "react";
import {
  FormElement,
  FormElementProps,
  FormElementRenderProps,
  FormWatcher,
  isValid,
} from "react-forms-state";
import { Dispatcher } from "react-forms-state/src/StateDispatcher";
import { useTranslations } from "translations";
import Translations from "translations/types";

type Item = {
  id: string;
  label: string;
  prerequisite?: boolean;
  serviceId: number;
  value?: boolean;
};

export type CheckboxGroupProps = {
  elementName?: string;
  exclude?: Array<number>;
  hideMargin?: boolean;
  include?: Array<number>;
  isRequired?: boolean;
  items: Item[];
  label: string;
  labelStyle?: React.CSSProperties;
  translations: Translations;
};

export function isChecked(value: any) {
  return value !== false && value != null;
}

const isRequisite = memoizeOne((serviceId) =>
  PREREQUISITES.includes(serviceId),
);

const CheckboxLabel = (label: number | string): React.JSX.Element => (
  <span style={{ fontSize: FONT_SIZE_14 }}>{label}</span>
);

const CheckboxField = FormElement()(function CheckboxInputField({
  elementName,
  hide,
  label,
  maxWidth = "400px",
  onBeforeChange,
  onChange,
  value,
  width = "100%",
}: FormElementProps & {
  classes?: AnyObject;
  elementName?: string;
  hide?: boolean;
  label: number | string;
  maxWidth?: string;
  onBeforeChange?: (value: number) => any;
  onChange?: (value: number) => void;
  value?: number | string;
  width?: string;
}) {
  if (hide) return null;

  const onChangeHandler = (e: ToType, checked: ToType) => {
    if (onChange && onBeforeChange) {
      onChange(onBeforeChange(checked));
    } else if (onChange) {
      onChange(checked);
    }
  };

  return (
    <>
      <FormControlLabel
        control={
          <Checkbox
            checked={isChecked(value)}
            color="primary"
            style={{
              height: space(4),
              padding: padding(0, 1.5),
            }}
            value={String(elementName)}
            onChange={onChangeHandler}
          />
        }
        label={CheckboxLabel(label)}
        name={String(elementName)}
        style={{
          maxWidth,
          width,
        }}
      />
    </>
  );
});

function CheckboxGroup({
  elementName,
  exclude,
  hideMargin,
  include,
  isRequired,
  items,
  label,
  labelStyle,
  translations,
}: CheckboxGroupProps) {
  const errorTextId = "services-checkbox-group-error-text";
  const checkboxes = items
    .filter((i) => !i.prerequisite)
    .filter((i) => (include?.length ? include.includes(i.serviceId) : true))
    .map((v) => (
      <Fragment key={`${elementName || "checkbox_group"}-${v.id}`}>
        <CheckboxField
          elementName={v.id.toString()}
          hide={exclude?.includes(v.serviceId) || false}
          label={v.label}
        />

        {isRequisite(v.serviceId) && (
          <FormWatcher watchPath={elementName as string}>
            {({ watchedValue }) => {
              const opened = watchedValue?.[v.serviceId]?.service === true;
              return (
                <div
                  style={{
                    height: !opened ? "0px" : undefined,
                    visibility: opened ? "visible" : "hidden",
                  }}
                >
                  <ToggleInputField
                    elementName={`${v.serviceId}-prerequisite`}
                    label={translations.careproviderProfile.prerequisites}
                  />
                </div>
              );
            }}
          </FormWatcher>
        )}
      </Fragment>
    ));

  return (
    <FormWatcher watchPath={elementName as string}>
      {({ watchedValidation }) => {
        const valid = isValid(watchedValidation);
        return (
          <FormControl component="fieldset" error={!valid}>
            <Subheading
              error={!valid}
              margin={hideMargin ? margin(1, 0) : undefined}
              style={labelStyle}
            >
              {labelWithOptionalRequiredIcon({ isRequired, label })}
            </Subheading>
            <div
              className={clsx("flex text-sm", { "ml-12": !hideMargin })}
              data-testid={`checkbox-group-${elementName}`}
            >
              <FormGroup>{checkboxes}</FormGroup>
            </div>
            <Validation
              elementName={elementName as string}
              hasCustomValidation
              id={errorTextId}
              validation={watchedValidation}
            />
          </FormControl>
        );
      }}
    </FormWatcher>
  );
}

export const ageServices = [14, 15, 16];

const computeItems = (
  order: AnyObject | undefined,
  getOntologies: GetOntologiesType,
) => {
  {
    const values = getOntologies({ type: "service" });

    const sortedValues = getOrderedItems(order, values).filter(
      (v) => !ageServices.includes(v.value),
    );

    const items = sortedValues.map((item) => ({
      id: `${item.value}`,
      label: item.label,
      serviceId: item.value,
    }));

    const concat = items.concat(
      sortedValues
        .filter((v) => isRequisite(v.value))
        .map((item) => ({
          id: `${item.value}-prerequisite`,
          label: item.label,
          prerequisite: true,
          serviceId: item.value,
        })),
    );

    return concat;
  }
};

const computeInValue = (
  inValue: Record<number, { prerequisite: undefined; service: boolean }>,
  items: Pick<Item, "id" | "serviceId" | "label">[],
): Item[] => {
  return items.map((v: ToType) => ({
    id: v.id,
    label: v.label,
    prerequisite: v.prerequisite || false,
    serviceId: v.serviceId,
    value: v.prerequisite
      ? inValue?.[v.serviceId]?.prerequisite === true || false
      : inValue?.[v.serviceId]?.service === true || false,
  }));
};

const computeOutValue = (outValue: ToType) => {
  const out = outValue
    .filter((v: ToType) => v.value)
    .reduce(function (output: ToType, item: ToType) {
      (output[item.serviceId] = output[item.serviceId] || []).push(item);
      return output;
    }, {});

  for (const key in out) {
    out[key] = out[key].reduce(
      (obj: AnyObject, s: ToType) =>
        Object.assign(
          obj,
          s.prerequisite ? { prerequisite: true } : { service: true },
        ),
      {},
    );
  }

  return out;
};

const getItems = memoizeOne(computeItems);
const memoizedComputeValue = memoizeOne(computeInValue);

export function ServicesCheckboxGroup({
  elementName,
  ...props
}: Omit<CheckboxGroupProps, "translations" | "items"> & {
  order?: AnyObject;
}) {
  const translations = useTranslations();
  const getOntologies = useGetOntologies();
  const items = getItems(props.order, getOntologies);

  return (
    <FormElementRenderProps elementName={elementName} root {...props}>
      {({ onChange, ...formElementProps }) => (
        <Dispatcher
          convertIn={(
            inValue: Record<
              number,
              { prerequisite: undefined; service: boolean }
            >,
          ) => memoizedComputeValue(inValue, items)}
          convertOut={computeOutValue}
          {...props}
          onChange={onChange}
          {...formElementProps}
        >
          <CheckboxGroup
            elementName={elementName}
            items={items}
            translations={translations}
            {...props}
          />
        </Dispatcher>
      )}
    </FormElementRenderProps>
  );
}
